import {
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry } from "ag-grid-community";
import {
  ClientSideRowModelModule,
  ValidationModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  PaginationModule,
  RowSelectionModule,
  TextEditorModule,
  SelectEditorModule,
  CustomEditorModule,
  NumberEditorModule,
  PinnedRowModule,
  CellStyleModule,
  DateEditorModule,
  RenderApiModule,
  RowAutoHeightModule,
  RowApiModule,
} from "ag-grid-community";
import { ColDef } from "ag-grid-community";
import { useSelector } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import CenteredLoader from "./CenteredLoader";

// --- Interfaces (Standard) ---
interface AgGridTableProps {
  rowData?: any[] | null;
  columnDefs?: ColDef[];
  height?: string;
  enableEditing?: boolean;
  enableSelection?: boolean;
  loading?: boolean;
  onCellValueChanged?: (event: any) => void;
  pinnedBottomRowData?: any[];
  reportHeader?: any;
  leftHeaders?: string[];
  rightHeaders?: string[];
  headerData?: string[];
  footerData?: string[];
  Caption?: any;
  RptColWidthInfo?: number[];
  RptColAlignInfo?: (
    | "left"
    | "center"
    | "right"
    | "justify"
    | "fill"
    | "centerContinuous"
    | "distributed"
  )[];
  CPrintName?: string;
  CAddress1?: string;
  CAddress2?: string;
  CAddress3?: string;
  CAddress4?: string;
  onRowClick?: (rowData: any) => void;
  cursor?: string;
  domLayout?: any;
}

interface RootState {
  theme: string;
  root: any;
}

type HAlignType = "left" | "center" | "right";
type VAlignType = "top" | "middle" | "bottom";

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ValidationModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  PaginationModule,
  RowSelectionModule,
  TextEditorModule,
  SelectEditorModule,
  CustomEditorModule,
  NumberEditorModule,
  PinnedRowModule,
  CellStyleModule,
  DateEditorModule,
  RenderApiModule,
  RowAutoHeightModule,
  RowApiModule,
]);

// --- Utility Functions ---
const formatCellValue = (value: any): string => {
  if (value === null || value === undefined) return "-";
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item === null || item === undefined) return "-";
        // Check for specific tokens that signal an empty or separator line
        if (typeof item !== "string" || item.trim() === ":" || item.trim() === "0.00 /-" || item.trim() === "0.00") return "";
        if (item.includes(":")) {
          const [label, val] = item.split(":", 2);
          return `${label.trim()}: ${val ? val.trim() : ""}`;
        }
        return item;
      })
      .filter((item) => item !== null && item !== undefined && item.trim() !== "")
      .join("\n");
  }
  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

// --- Component Definition ---
const ImageReportTable = forwardRef(
  (
    {
      rowData = [],
      columnDefs = [],
      height = "600px",
      enableEditing = false,
      enableSelection = false,
      onCellValueChanged,
      pinnedBottomRowData = [],
      reportHeader = { RptName: "" },
      leftHeaders = [],
      rightHeaders = [],
      headerData = [],
      footerData = [],
      Caption,
      RptColWidthInfo = [],
      RptColAlignInfo = [],
      CPrintName = "",
      CAddress1 = "",
      CAddress2 = "",
      CAddress3 = "",
      CAddress4 = "",
      cursor,
      onRowClick,
      domLayout = "normal",
    }: AgGridTableProps,
    ref
  ) => {
    const gridRef = useRef<AgGridReact>(null);
    const theme = useSelector((state: RootState) => state.root.theme);
    const fullExportColumnDefs = columnDefs.filter((col) => col.field !== "action");
    const [isExportingPDF, setIsExportingPDF] = useState(false);

    const defaultColDef = useMemo<ColDef>(
      () => ({
        sortable: true,
        filter: true,
        resizable: true,
        editable: enableEditing,
        cellStyle: (params) => {
          const colIndex = columnDefs.findIndex(
            (col) => col.field === params.colDef.field
          );
          const alignment =
            colIndex >= 0 && colIndex < RptColAlignInfo.length
              ? RptColAlignInfo[colIndex] || "left"
              : "left";
          return {
            display: "flex",
            alignItems: "center",
            justifyContent: alignment === "center" ? "center" : alignment,
            textAlign: alignment,
            minHeight: "80px",
          };
        },
      }),
      [enableEditing, RptColAlignInfo, columnDefs]
    );

    // --- Helper function to get alignment based on column type ---
    const getAlignmentForColumn = useCallback(
      (colIndex: number, _totalCols: number, exportCols: ColDef[]) => {
        const col = exportCols[colIndex];
        const field = col.field || "";
        const headerName = col.headerName || "";

        if (field.includes("image_")) {
          return "center";
        }

        if (headerName === "Product Details" || field.includes("detail_")) {
          return "left";
        }

        if (headerName === "Total Available Stock" || field.includes("stock_")) {
          return "center";
        }

        return "left";
      },
      []
    );

    // --- Helper function to RE-PAIR selected items in left-right layout ---
    const getSelectedPairedData = useCallback(() => {
      const selectedItems: any[] = [];
      const iterableRowData = rowData || [];

      // Step 1: Collect all selected items (flattened)
      iterableRowData.forEach((pairedRow: any) => {
        const leftItem = pairedRow.left;
        const rightItem = pairedRow.right;

        // Collect left item if selected
        if (leftItem && leftItem.IsSelected === 1) {
          selectedItems.push({
            originalData: leftItem,
            side: "left",
            pairedRow: pairedRow,
          });
        }

        // Collect right item if selected
        if (rightItem && rightItem.IsSelected === 1 && rightItem.id !== undefined) {
          selectedItems.push({
            originalData: rightItem,
            side: "right",
            pairedRow: pairedRow,
          });
        }
      });

      // Step 2: Re-pair them in left-right-left-right order
      const repairedData: any[] = [];

      for (let i = 0; i < selectedItems.length; i += 2) {
        const leftSelectedItem = selectedItems[i];
        const rightSelectedItem = selectedItems[i + 1];

        const newPairedRow: any = {};

        // Process left item
        if (leftSelectedItem) {
          const leftPairedRow = leftSelectedItem.pairedRow;
          const leftSide = leftSelectedItem.side;

          newPairedRow.left = leftSelectedItem.originalData;
          newPairedRow.image_left = leftSelectedItem.originalData.image || "";

          // Copy all left column data from the original side
          Object.keys(leftPairedRow).forEach((key) => {
            if (key.endsWith(`_${leftSide}`)) {
              const newKey = key.replace(`_${leftSide}`, "_left");
              newPairedRow[newKey] = leftPairedRow[key];
            }
          });
        }

        // Process right item (if exists)
        if (rightSelectedItem) {
          const rightPairedRow = rightSelectedItem.pairedRow;
          const rightSide = rightSelectedItem.side;

          newPairedRow.right = rightSelectedItem.originalData;
          newPairedRow.image_right = rightSelectedItem.originalData.image || "";

          // Copy all right column data from the original side
          Object.keys(rightPairedRow).forEach((key) => {
            if (key.endsWith(`_${rightSide}`)) {
              const newKey = key.replace(`_${rightSide}`, "_right");
              newPairedRow[newKey] = rightPairedRow[key];
            }
          });
        } else {
          // No right item - fill with empty data
          newPairedRow.right = { id: undefined, image: "", IsSelected: 0 };
          newPairedRow.image_right = "";

          // Empty all right columns
          // This ensures that for the last odd item, the right columns are empty in the export
          Object.keys(newPairedRow).forEach((key) => {
            if (key.endsWith("_left") && !newPairedRow[key.replace("_left", "_right")]) {
              const rightKey = key.replace("_left", "_right");
              newPairedRow[rightKey] = "";
            }
          });
        }

        repairedData.push(newPairedRow);
      }

      return repairedData;
    }, [rowData]);

    // --- EXCEL Export Logic ---
    const onExportExcel = useCallback(async () => {
      const allRowData = getSelectedPairedData();

      if (allRowData.length === 0) {
        alert("No items selected for export.");
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Report");

      const exportCols = fullExportColumnDefs;
      const headers = exportCols.map((col) => col.headerName || col.field || "");

      const CHAR_WIDTH = 10;
      const MAX_WIDTH = 300;
      const EXCEL_WIDTH_FACTOR = 0.25;

      const columnWidths = exportCols.map((_col, index) => {
        const colsPerSide = exportCols.length / 2;
        const effectiveIndex = index < colsPerSide ? index : index - colsPerSide;
        const charCount = RptColWidthInfo[effectiveIndex] || 15;
        const pixelWidth = Math.min(charCount * CHAR_WIDTH, MAX_WIDTH);
        return Math.round(pixelWidth * EXCEL_WIDTH_FACTOR);
      });

      worksheet.columns = exportCols.map((_, index) => ({
        width: columnWidths[index] !== undefined ? columnWidths[index] : 20,
      }));

      const headerColor = "FF4169E1";
      let currentRow = 1;

      if (CPrintName) {
        const row = worksheet.addRow([CPrintName]);
        row.getCell(1).style = {
          font: { bold: true, size: 16, color: { argb: "FF000000" } },
          alignment: { horizontal: "center", vertical: "middle" },
          fill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF0F0F0" },
          },
        };
        worksheet.mergeCells(currentRow, 1, currentRow, exportCols.length);
        currentRow++;
      }
      if (CAddress1 || CAddress2) {
        const addressRow1 = worksheet.addRow([
          [CAddress1, CAddress2].filter(Boolean).join(", "),
        ]);
        addressRow1.getCell(1).style = {
          font: { bold: true, size: 10, color: { argb: "FF666666" } },
          alignment: { horizontal: "center", vertical: "middle" },
        };
        worksheet.mergeCells(currentRow, 1, currentRow, exportCols.length);
        currentRow++;
      }

      if (CAddress3 || CAddress4) {
        const addressRow2 = worksheet.addRow([
          [CAddress3, CAddress4].filter(Boolean).join(", "),
        ]);
        addressRow2.getCell(1).style = {
          font: { bold: true, size: 10, color: { argb: "FF666666" } },
          alignment: { horizontal: "center", vertical: "middle" },
        };
        worksheet.mergeCells(currentRow, 1, currentRow, exportCols.length);
        currentRow++;
      }

      if (reportHeader?.RptName) {
        const row = worksheet.addRow([reportHeader.RptName]);
        row.getCell(1).style = {
          font: { bold: true, size: 14, color: { argb: "FF000000" } },
          alignment: { horizontal: "center", vertical: "middle" },
          fill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF0F0F0" },
          },
        };
        worksheet.mergeCells(currentRow, 1, currentRow, exportCols.length);
        currentRow++;
      }

      const maxHeaderLength = Math.max(leftHeaders.length, rightHeaders.length);
      if (maxHeaderLength > 0) {
        for (let i = 0; i < maxHeaderLength; i++) {
          const row = worksheet.addRow([...Array(exportCols.length).fill("")]);
          if (leftHeaders[i]) {
            row.getCell(1).value = leftHeaders[i];
            row.getCell(1).style = {
              font: { bold: true, size: 10, color: { argb: "FF666666" } },
              alignment: { horizontal: "left", vertical: "middle" },
            };
          }
          if (rightHeaders[i]) {
            row.getCell(Math.floor(exportCols.length / 2) + 1).value =
              rightHeaders[i];
            row.getCell(Math.floor(exportCols.length / 2) + 1).style = {
              font: { bold: true, size: 10, color: { argb: "FF666666" } },
              alignment: { horizontal: "right", vertical: "middle" },
            };
          }
          currentRow++;
        }
        if (leftHeaders.length > 1) {
          worksheet.mergeCells(
            currentRow - maxHeaderLength,
            1,
            currentRow - 1,
            1
          );
        }
        if (rightHeaders.length > 1) {
          worksheet.mergeCells(
            currentRow - maxHeaderLength,
            Math.floor(exportCols.length / 2) + 1,
            currentRow - 1,
            Math.floor(exportCols.length / 2) + 1
          );
        }
      }

      if (headerData?.length > 0) {
        headerData.forEach((header) => {
          const row = worksheet.addRow([
            ...Array(exportCols.length - 1).fill(""),
            header,
          ]);
          row.getCell(exportCols.length).style = {
            font: { bold: true, color: { argb: "FF666666" } },
            alignment: { horizontal: "right", vertical: "middle" },
          };
          currentRow++;
        });
      }

      const headerRow = worksheet.addRow(headers);
      headers.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.style = {
          font: { bold: true, color: { argb: "FFFFFFFF" } },
          fill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: headerColor },
          },
          alignment: {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          },
          border: {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          },
        };
      });
      currentRow++;

      // --- Table Body Data ---
      allRowData.forEach((row: any) => {
        const rowData = exportCols.map((col) => {
          const value = row[col.field || ""] || "";
          if (col.field?.includes("image_")) return value;
          return formatCellValue(value);
        });
        const excelRow = worksheet.addRow(rowData);
        rowData.forEach((_, colIndex) => {
          const alignment = getAlignmentForColumn(
            colIndex,
            exportCols.length,
            exportCols
          );

          excelRow.getCell(colIndex + 1).style = {
            alignment: {
              horizontal: alignment as any,
              vertical: "top", // Force top alignment for multi-line data
              wrapText: true,
            },
          };
        });
        currentRow++;
      });

      if (pinnedBottomRowData?.length) {
        pinnedBottomRowData.forEach((row: { [x: string]: any }) => {
          const rowData = exportCols.map((col) => {
            const value = row[col.field || ""] || "";
            if (col.field?.includes("image_")) return "";
            return formatCellValue(value);
          });
          const excelRow = worksheet.addRow(rowData);
          rowData.forEach((_, colIndex) => {
            const alignment = getAlignmentForColumn(
              colIndex,
              exportCols.length,
              exportCols
            );

            excelRow.getCell(colIndex + 1).style = {
              alignment: {
                horizontal: alignment as any,
                vertical: "middle",
                wrapText: true,
              },
            };
          });
          currentRow++;
        });
      }

      if (footerData?.length > 0) {
        footerData.forEach((footer) => {
          const row = worksheet.addRow([
            ...Array(exportCols.length - 1).fill(""),
            footer,
          ]);
          row.getCell(exportCols.length).style = {
            font: { bold: true, color: { argb: "FF666666" } },
            alignment: { horizontal: "right", vertical: "middle" },
          };
          currentRow++;
        });
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      saveAs(blob, `${Caption || reportHeader?.RptName}.xlsx`);
    }, [
      getSelectedPairedData,
      getAlignmentForColumn,
      pinnedBottomRowData,
      reportHeader,
      leftHeaders,
      rightHeaders,
      headerData,
      footerData,
      Caption,
      RptColWidthInfo,
      CPrintName,
      CAddress1,
      CAddress2,
      CAddress3,
      CAddress4,
      fullExportColumnDefs,
    ]);

    // --- PDF Export Logic ---
    const onExportPDF = useCallback(async () => {
      const allRowData = getSelectedPairedData();

      if (allRowData.length === 0) {
        alert("No items selected for export.");
        return;
      }

      setIsExportingPDF(true);
      try {
        const CHAR_WIDTH = 10;
        const MAX_WIDTH = 500;
        const MM_PER_PIXEL = 0.264583;
        // Adjusted minimum column width for better handling of image/details columns
        const MIN_COLUMN_WIDTH = 20; 
        const MAX_COLUMN_WIDTH = 100; // Increased maximum width

        const exportCols = fullExportColumnDefs;
        const totalColumns = exportCols.length;

        const columnWidths = exportCols.map((col, index) => {
          if (col.field?.includes("image_")) {
            // Give Image column a fixed initial width (e.g., 30mm)
            return 30; 
          }
          const colsPerSide = exportCols.length / 2;
          const effectiveIndex = index < colsPerSide ? index : index - colsPerSide;
          const charCount = RptColWidthInfo[effectiveIndex] || 15;
          const pixelWidth = Math.min(charCount * CHAR_WIDTH, MAX_WIDTH);
          // Convert calculated pixel width to mm
          return pixelWidth * MM_PER_PIXEL;
        });

        const doc = new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: "a4",
        });
        const pageWidth = doc.internal.pageSize.getWidth() - 20; // 10mm margin on each side

        // Apply min/max constraints and scale to fit page width
        let adjustedColumnWidths = columnWidths.map((width) =>
          Math.min(Math.max(width, MIN_COLUMN_WIDTH), MAX_COLUMN_WIDTH)
        );
        let adjustedTotalWidth = adjustedColumnWidths.reduce(
          (sum, width) => sum + width,
          0
        );

        // If total width exceeds page width, scale down proportionally
        if (adjustedTotalWidth > pageWidth) {
          const scaleFactor = pageWidth / adjustedTotalWidth;
          adjustedColumnWidths = adjustedColumnWidths.map(
            (width) => width * scaleFactor
          );
        } else {
             // If too narrow, distribute remaining space. This helps fill the page.
             const remainingWidth = pageWidth - adjustedTotalWidth;
             const detailColumnsCount = adjustedColumnWidths.filter((_, index) => !exportCols[index].field?.includes("image_")).length;
             const bonusPerDetailCol = detailColumnsCount > 0 ? remainingWidth / detailColumnsCount : 0;

             adjustedColumnWidths = adjustedColumnWidths.map((width, index) => {
                 if (!exportCols[index].field?.includes("image_")) {
                     return width + bonusPerDetailCol;
                 }
                 return width;
             });
        }
        
        // Recalculate total width after distribution/scaling
        adjustedTotalWidth = adjustedColumnWidths.reduce(
          (sum, width) => sum + width,
          0
        );


        const columnAlignments = exportCols.map((_col, index) => {
          const alignment = getAlignmentForColumn(
            index,
            exportCols.length,
            exportCols
          );
          return ["left", "center", "right"].includes(alignment)
            ? (alignment as HAlignType)
            : "left";
        });

        const fontSize = totalColumns > 10 ? 7 : totalColumns > 6 ? 8 : 10;
        const headerHeightPx = 16 + 4 * 2.25;
        const MM_PER_PX = 0.264583;
        const headerHeightMm = headerHeightPx * MM_PER_PX;

        const headers = [
          exportCols.map((col) => col.headerName || col.field || ""),
        ];

        const imageColIndices = exportCols
          .map((col, index) => (col.field?.includes("image_") ? index : -1))
          .filter((index) => index !== -1);

        let imageMap = new Map<string, string>();
        if (imageColIndices.length > 0) {
          const uniqueImageUrls = new Set<string>();
          allRowData.forEach((row: any) => {
            imageColIndices.forEach((colIndex) => {
              const field = exportCols[colIndex].field;
              const imgUrl = row[field || ""];
              if (imgUrl) uniqueImageUrls.add(imgUrl);
            });
          });

          // Fetch images and convert to Base64
          const loadPromises = Array.from(uniqueImageUrls).map(
            async (url: string) => {
              try {
                // The images in the screenshot look like placeholders or local/intranet URLs,
                // so fetching might fail. A try/catch is essential.
                const response = await fetch(url, { mode: "cors" });
                if (!response.ok) return;
                const blob = await response.blob();
                const base64 = await new Promise<string>((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result as string);
                  reader.onerror = reject;
                  reader.readAsDataURL(blob);
                });
                imageMap.set(url, base64);
              } catch (e) {
                console.error(`Failed to load image ${url}:`, e);
              }
            }
          );
          await Promise.all(loadPromises);
        }

        const body = allRowData.map((row: any) =>
          exportCols.map((col, colIndex) => {
            const value = row[col.field || ""] || "";
            if (imageColIndices.includes(colIndex)) {
              return value; // Keep the URL/path for didDrawCell
            }
            return formatCellValue(value); // Format text details
          })
        );

        let isFirstPage = true;
        let currentY = 10;

        // --- Header Content Drawing ---
        if (isFirstPage) {
          if (CPrintName) {
            doc.setFontSize(16);
            doc.setFont("helvetica", "bold");
            doc.text(CPrintName, pageWidth / 2 + 10, currentY, {
              align: "center",
            });
            currentY += 8;
          }
          if (CAddress1 || CAddress2) {
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(100, 100, 100);
            const address1 = [CAddress1, CAddress2].filter(Boolean).join(", ");
            if (address1) {
              doc.text(address1, pageWidth / 2 + 10, currentY, {
                align: "center",
              });
            }
            currentY += 6;
          }
          if (CAddress3 || CAddress4) {
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(100, 100, 100);
            const address2 = [CAddress3, CAddress4].filter(Boolean).join(", ");
            if (address2) {
              doc.text(address2, pageWidth / 2 + 10, currentY, {
                align: "center",
              });
            }
            currentY += 6;
          }
          if (reportHeader?.RptName) {
            doc.setFontSize(14);
            doc.setFont("helvetica", "bold");
            doc.text(reportHeader.RptName, pageWidth / 2 + 10, currentY, {
              align: "center",
            });
            currentY += 8;
            doc.setFontSize(12);
          }
          if (leftHeaders.length > 0 || rightHeaders.length > 0) {
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(100, 100, 100);
            const maxHeaderLength = Math.max(
              leftHeaders.length,
              rightHeaders.length
            );
            for (let i = 0; i < maxHeaderLength; i++) {
              if (leftHeaders[i]) {
                doc.text(leftHeaders[i], 10, currentY);
              }
              if (rightHeaders[i]) {
                doc.text(rightHeaders[i], pageWidth + 10, currentY, {
                  align: "right",
                });
              }
              currentY += 6;
            }
          }
          if (headerData?.length > 0) {
            doc.setFontSize(10);
            doc.setFont("helvetica", "bold");
            headerData.forEach((header) => {
              doc.text(header, pageWidth + 10, currentY, { align: "right" });
              currentY += 6;
            });
          }
        }

        // --- AutoTable Generation ---
        autoTable(doc, {
          head: headers,
          body: body,
          theme: "striped",
          styles: {
            fontSize: fontSize,
            cellPadding: 2,
            overflow: "linebreak",
            // Set min height to accommodate an image, allowing text cells to wrap/expand if necessary
            minCellHeight: 25, 
            valign: "middle",
            fontStyle: "bold",
          },
          headStyles: {
            fillColor: [0, 69, 136], // Blue color from Ag-Grid theme
            textColor: [255, 255, 255],
            fontStyle: "bold",
            minCellHeight: headerHeightMm,
            valign: "middle",
          },
          columnStyles: exportCols.reduce((acc, col, index) => {
            const isProductDetailColumn = col.headerName === "Product Details";
            // const isImageColumn = col.field?.includes("image_");

            acc[index.toString()] = {
              cellWidth: adjustedColumnWidths[index],
              halign: columnAlignments[index] || "left",
              // IMPORTANT: Use 'top' for details for multi-line text alignment
              valign: (isProductDetailColumn ? "top" : "middle") as VAlignType,
              // Add a vertical separator between the left and right pair
              ...(index === Math.floor(totalColumns / 2) - 1
                ? {
                    cellBorder: {
                      right: { style: "solid", color: [0, 0, 0], width: 0.5 },
                    },
                  }
                : {}),
            };
            return acc;
          }, {} as { [key: string]: { cellWidth: number; halign: HAlignType; valign?: VAlignType; cellBorder?: { right?: { style: string; color: number[]; width: number } } } }),
          margin: { top: currentY, left: 10, right: 10 },
          pageBreak: "auto",
          rowPageBreak: "avoid",
          tableWidth: adjustedTotalWidth, // Use the adjusted total width
          // Set text to empty for image cells so autotable doesn't try to render the URL
          didParseCell: (data) => {
            if (
              imageColIndices.includes(data.column.index) &&
              data.cell.section === "body"
            ) {
              data.cell.text = [];
            }
          },
          // Draw the image inside the cell
          didDrawCell: (data) => {
            if (
              imageColIndices.includes(data.column.index) &&
              data.cell.section === "body"
            ) {
              const imgUrl = data.cell.raw as string;
              const base64 = imageMap.get(imgUrl);
              if (base64) {
                try {
                  const imgProps = doc.getImageProperties(base64);
                  const cellWidth = data.cell.width - 4; // Minus padding
                  const cellHeight = data.cell.height - 4; // Minus padding
                  let imgWidth = cellWidth;
                  let imgHeight = imgWidth * (imgProps.height / imgProps.width);
                  
                  // Scale image down if it exceeds cell height
                  if (imgHeight > cellHeight) {
                    imgHeight = cellHeight;
                    imgWidth = imgHeight * (imgProps.width / imgProps.height);
                  }
                  
                  const alignment = columnAlignments[data.column.index] || "center";
                  let xOffset = (data.cell.width - imgWidth) / 2; // Center default
                  
                  if (alignment === "left") {
                    xOffset = 2;
                  } else if (alignment === "right") {
                    xOffset = data.cell.width - imgWidth - 2;
                  }
                  const yOffset = (data.cell.height - imgHeight) / 2;
                  
                  doc.addImage(
                    base64,
                    data.cell.x + xOffset,
                    data.cell.y + yOffset,
                    imgWidth,
                    imgHeight
                  );
                } catch (e) {
                  console.error("Error adding image to PDF:", e);
                }
              } else {
                 // Draw "No Image" placeholder text if loading failed
                 doc.setFontSize(10);
                 doc.setTextColor(150);
                 doc.text("No Image", data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2, { align: "center", baseline: "middle" });
              }
            }
          },
          didDrawPage: (data) => {
            isFirstPage = false;
            doc.setFontSize(8);
            doc.text(
              `Page ${data.pageNumber}`,
              doc.internal.pageSize.getWidth() - 20,
              doc.internal.pageSize.getHeight() - 10
            );
            data.settings.margin.top = 10;
          },
        });

        // --- Footer Content Drawing ---
        if (footerData?.length > 0) {
          const tableHeight = doc.lastAutoTable.finalY || currentY;
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(100, 100, 100);
          footerData.forEach((footer, index) => {
            doc.text(footer, pageWidth + 10, tableHeight + 6 + index * 6, {
              align: "right",
            });
          });
        }

        doc.save(`${Caption || "Report"}.pdf`);
      } catch (error) {
        console.error("Error generating PDF:", error);
      } finally {
        setIsExportingPDF(false);
      }
    }, [
      getSelectedPairedData,
      getAlignmentForColumn,
      reportHeader,
      leftHeaders,
      rightHeaders,
      headerData,
      footerData,
      Caption,
      RptColWidthInfo,
      CPrintName,
      CAddress1,
      CAddress2,
      CAddress3,
      CAddress4,
      fullExportColumnDefs,
    ]);

    useImperativeHandle(ref, () => ({
      exportToExcel: onExportExcel,
      exportToPDF: onExportPDF,
    }));

    const onSelectionChanged = useCallback(() => {
      if (enableSelection) {
        // You may want to update the parent component's state with selected rows here
        // const selectedRows = gridRef.current?.api.getSelectedRows();
        // console.log("Selected Rows:", selectedRows);
      }
    }, [enableSelection]);

    const themeClass =
      theme === "dark_mode" ? "ag-theme-quartz-dark" : "ag-theme-quartz";

    return (
      <>
        <style>
          {`
            .ag-theme-quartz-dark {
              --ag-background-color: #1d1d42 !important;
              --ag-header-background-color: #1d1d42 !important;
              --ag-row-hover-color: rgba(255, 255, 255, 0.1) !important;
              --ag-column-hover-color: rgba(255, 255, 255, 0.1) !important;
              --ag-row-background-color: #2a2a4a !important;
              --ag-odd-row-background-color: #3a3a5a !important;
              --ag-row-height: 80px !important;
              --ag-header-height: calc(var(--ag-font-size) + var(--ag-grid-size) * 2.25);
            }
            .ag-theme-quartz {
              --ag-header-background-color: #004588 !important;
              --ag-header-foreground-color: #ffffff !important;
              --ag-row-background-color: #ffffff !important;
              --ag-odd-row-background-color: #f0f4f8 !important;
              --ag-row-height: 80px !important;
              --ag-header-height: calc(var(--ag-font-size) + var(--ag-grid-size) * 2.25);
              --ag-header-column-resize-handle-color: #ffffff !important;
            }
            .ag-theme-quartz .ag-icon-filter {
              color: #ffffff !important;
            }
            .ag-theme-quartz .ag-header-cell .ag-filter-icon .ag-icon {
              fill: #ffffff !important;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            .${themeClass} .ag-row, .${themeClass} .ag-row-even, .${themeClass} .ag-row-odd {
              cursor: ${cursor !== undefined ? "pointer" : "default"} !important;
            }
            .${themeClass} .ag-row:hover {
              background-color: ${theme === "dark_mode"
                ? "rgba(255, 255, 255, 0.2)"
                : "rgba(0, 0, 0, 0.05)"
              } !important;
            }
          `}
        </style>
        {isExportingPDF && <CenteredLoader />}
        <div className={`table-responsive ${themeClass}`} style={{ height }}>
          <AgGridReact
            suppressDragLeaveHidesColumns={true}
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={false}
            paginationPageSize={10}
            paginationPageSizeSelector={[10, 25, 50, 100]}
            animateRows={true}
            rowModelType="clientSide"
            theme="legacy"
            rowSelection={enableSelection ? "multiple" : undefined}
            onSelectionChanged={
              enableSelection ? onSelectionChanged : undefined
            }
            onCellValueChanged={onCellValueChanged}
            pinnedBottomRowData={pinnedBottomRowData}
            onCellClicked={
              onRowClick
                ? (event) => {
                    // Check if the click occurred on a data row and not a group/footer row
                    if (event && event.data) {
                      onRowClick(event.data);
                    }
                  }
                : undefined
            }
            domLayout={domLayout}
          />
        </div>
      </>
    );
  }
);

export default ImageReportTable;
